//library
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

//dto
import SolutionGroupTableProps, { SolutionGroupDataDto, SolutionGroupUpdateDto } from "dto/components/pages/solution-group/app-solution-group-table-dto";

import { statusOptions } from "constants/status-options";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//common
import formatIndexNumber from "common/format-index-number";

//assets
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
// import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
// import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
// import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";

const SolutionGroupTable: React.FC<SolutionGroupTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdatePartners, onCreatePartner, onHandlePageChange, fetching, paginationTable }) => {
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [categories, setCategories] = useState<SolutionGroupDataDto>(data);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const [updatedData, setUpdatedData] = useState<SolutionGroupUpdateDto>();
	const isEmptyData = useMemo(() => categories.length === 0 && !fetching, [categories.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "title" | "status" , value: string) => {
		const updatedCategories = updatedData ?? categories.find((category) => category.id === id);

		if (updatedCategories) {
			const updatedData = { ...updatedCategories, [field]: value};

			setUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
    }, [categories, updatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["title", "status"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (updatedData) {
			const rowErrors = validateRow(updatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newCategory = categories.map((category) => (category.id === id ? { ...category, ...updatedData } : category));

			setCategories(newCategory);
			onUpdatePartners(updatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [categories, updatedData, onUpdatePartners, setDisableAddNew]);

	//prettier-ignore
	// const onHandleMove = useCallback((id: string, move: number) => {
	// 	const index = categories.findIndex((category) => category.id === id);
	// 	const updatedPartner = categories[index];

	// 	if ((index > 0  && move === -1) || (index < categories.length - 1 && move === 1)) {
	// 		const newPartners = [...categories];
	// 		const [movedCareer] = newPartners.splice(index, 1);
	// 		const movedDataSortNumber = categories[index + move].sortNumber;
	// 		const updatedData = { ...updatedPartner, sortNumber: movedDataSortNumber };

	// 		if (updatedData) setUpdatedData(updatedData);

	// 		newPartners.splice(index + move, 0, movedCareer);

	// 	 setCategories(newPartners);
	// 	}
	// }, [categories]);

	//prettier-ignore
	// const onHandleDelete = useCallback((id: string) => {
	// 	const updatedPartner = categories.find((category) => category.id === id);

	// 	if (updatedPartner) {
	//         const updatedData = { ...updatedPartner, status: "inactive" };

	// 		const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

	// 		onUpdatePartners(updatedData, changePage);
	// 	}

	// 	setEditingRow(null);
	// 	setUpdatedData(null);
	// 	setDisableAddNew(false);
	// }, [categories, paginationTable, onUpdatePartners, setDisableAddNew]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setEditingRow(null);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newCategory = {
			title: newRow?.title || "",
			status: "active",
		};

		onCreatePartner(newCategory);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setCategories(data);
	}, [categories, editingRow, isReadyData, data]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-solution-group-table">
			<div className="group-table">
				<div className="group-table__wrapper">
					<table className="group-table__container">
						<thead className="group-table__header">
							<tr>
								<th className="group-table__th">No.</th>
								<th className="group-table__th">Title</th>
								<th className="group-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="group-table__content">
							{categories.map((item, index) => {
								const isEditing = editingRow === item.id;
								const isExpanded = expandedRows.includes(item.id);
								const rowErrors = errors[item.id] || {};

								return (
									<tr className="group-table__tr" key={item.id}>
										<td className="group-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="group-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!updatedData} value={updatedData?.title ?? item.title} maxCharLength={50} maxRows={20} onChange={(e) => onHandleInputChange(item.id, "title", e.target.value)} />
											) : isExpanded ? (
												item.title
											) : (
												truncateText(item.title, 60)
											)}
										</td>

										<td className="group-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="status" error={rowErrors.status} touched={!!updatedData} placeholder={item.status} value={updatedData?.status ?? item.status} onChange={(e) => onHandleInputChange(item.id, "status", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={item.status === "active" ? "group-table__td--active" : "group-table__td--inactive"}>{getLabel(statusOptions, item.status)}</span>
											)}
										</td>

										<td className="group-table__td">
											{isEditing ? (
												<div className="group-table__edit">
													<button className="group-table__cancel" onClick={onHandleCancel}>
														Cancel
													</button>

													{/* <button className="group-table__icon-button--bordered" onClick={() => onHandleDelete(item.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button> */}

													{/* <button className="group-table__icon-button--bordered" onClick={() => onHandleMove(item.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="group-table__icon-button--bordered" onClick={() => onHandleMove(item.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button> */}

													<button className="group-table__icon-button--save" onClick={() => onHandleSave(item.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="group-table__actions">
													<button className="group-table__icon-button" onClick={() => toggleRow(item.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="group-table__icon-button" onClick={() => onHandleEdit(item.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="group-table__tr" ref={newRowRef}>
									<td className="group-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="group-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-title" error={errors["new"]?.title} touched={!!newRow} fieldRequired value={newRow?.title || ""} maxCharLength={50} maxRows={20} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="group-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="status" error={errors["new"]?.status} touched={!!newRow} placeholder="Please Select" value={newRow?.status || ""} onChange={(e) => onHandleNewInputChange("status", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="group-table__td">
										<div className="group-table__actions">
											<button className="group-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="group-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="group-table__tr">
									<td className="group-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}
							{isEmptyData && (
								<tr className="group-table__tr">
									<td className="group-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default SolutionGroupTable;
